'use client';

/**
 * useCountryCatalogue — the country list the sign-up form offers.
 *
 * Backed by `GET /api/v1/jurisdictions/catalogue`, which is unauthenticated
 * on purpose: the form has to render before there is an account, and the
 * country chosen there is what the backend's `editions.org_defaults` derives
 * `preferred_jurisdiction` and `billing_currency` from.
 *
 * Two lists come back and they are not the same thing:
 *
 *   - `countries_with_depth` — the corpus holds jurisdiction-specific law for
 *     these (the launch set, flattened to country level because a signup form
 *     asks for a country and `US-DE` is not one).
 *   - `countries_supported`  — everywhere else we can bill. Currency works;
 *     there is no local-law depth behind it.
 *
 * We offer **only the first list**, so the picker never implies coverage we
 * don't have. Anyone else picks "Other" and gets no country at all, which the
 * backend reads as an honest unknown jurisdiction (general commercial-contract
 * analysis) rather than a guess — see `jurisdiction_for_country`.
 *
 * Country *names* are deliberately not fetched: the browser renders them from
 * the ISO codes in the viewer's own locale, which is one less list to keep
 * correct in two places.
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch, NetworkError } from '@/lib/api';

export const COUNTRY_CATALOGUE_QUERY_KEY = ['jurisdictions', 'catalogue'] as const;

/** Sentinel for "my country isn't listed" — never sent to the backend. */
export const COUNTRY_OTHER = '__other__';

export type LegalFamily = 'common_law' | 'civil_law' | 'mixed' | 'supranational';

export interface CatalogueJurisdiction {
  /** ISO-3166 alpha-2, an ISO 3166-2 sub-division (`US-DE`), or a named zone (`EU`). */
  code: string;
  name: string;
  /** ISO-2 country this sits in. Null where the code is not a country (`EU`). */
  country: string | null;
  legal_family: LegalFamily;
  in_launch_set: boolean;
  /** Whether a claim about this jurisdiction can reach `verified`. */
  has_adapter: boolean;
  note: string | null;
}

export interface JurisdictionCatalogue {
  jurisdictions: CatalogueJurisdiction[];
  countries_with_depth: string[];
  countries_supported: string[];
  adapters: string[];
}

export interface CountryOption {
  /** ISO-3166 alpha-2 — exactly what `POST /auth/signup/request-code` wants. */
  code: string;
  /** Localised country name, or the bare code if `Intl.DisplayNames` is absent. */
  label: string;
  /**
   * Named sub-jurisdictions the corpus actually covers, where coverage is
   * narrower than the country: `US` is Delaware / New York / California, not
   * "the United States". Empty when the country itself is the unit.
   */
  covers: string[];
}

function regionName(code: string): string {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function toOptions(cat: JurisdictionCatalogue): CountryOption[] {
  return cat.countries_with_depth
    .map((code) => {
      const inCountry = cat.jurisdictions.filter(
        (j) => j.country === code && j.in_launch_set,
      );
      // Only worth naming when the country isn't itself the registry code —
      // i.e. coverage is sub-national (US-DE, CA-ON, AE-DIFC).
      // Registry names carry the country in parentheses ("Delaware (United
      // States)") because they also appear alone. Under a country row that is
      // three repetitions of the row's own label, so trim it.
      const covers = inCountry.some((j) => j.code === code)
        ? []
        : inCountry.map((j) => j.name.replace(/\s*\([^)]*\)\s*$/, ''));
      return { code, label: regionName(code), covers };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function useCountryCatalogue() {
  const query = useQuery({
    queryKey: COUNTRY_CATALOGUE_QUERY_KEY,
    queryFn: ({ signal }) =>
      apiFetch<JurisdictionCatalogue>('/jurisdictions/catalogue', {
        signal,
        skipAuth: true,
      }),
    // The registry only changes when we launch a jurisdiction.
    staleTime: 24 * 60 * 60_000,
    retry: (failureCount, error) => (error instanceof NetworkError ? failureCount < 1 : false),
  });

  return {
    ...query,
    /** Countries we hold law for, localised and sorted. Empty while loading. */
    options: query.data ? toOptions(query.data) : [],
  };
}
